import { Pagination, Spin, Checkbox, Empty } from "antd";
import { Clock, FileSignature, Truck, User } from "lucide-react";
import { useMemo, useState, useEffect } from "react";
import { useDebounceValue } from "~/hooks/use-debounce";
import { useGetBookings } from "~/services/booking/booking.queries";
import { TBooking } from "~/types/booking";
import { capitalize } from "~/utils";
import { SortOrder } from "~/utils/constants";
import { TInventory } from "~/types/inventory";
import { useInventoryControlStore } from "./inventory-control.store";
import dayjs from "dayjs";

interface InventoryControlByBookingProps {
  onSelectedInventories: (inventories: TInventory[]) => void;
}

export const InventoryControlByBooking: React.FC<
  InventoryControlByBookingProps
> = ({ onSelectedInventories }) => {
  // Get state from Zustand store
  const {
    filters: { selectedDate, selectedClient, searchTerm },
    selectedItems: selectedServiceTickets,
    setSelectedItems: setSelectedServiceTickets,
  } = useInventoryControlStore();
  const [page, setPage] = useState(1);

  const [search] = useDebounceValue(searchTerm, 500);

  const filterParams = useMemo(() => {
    const params: Record<string, any> = {
      page,
      pageSize: 10,
    };
    if (search) {
      params.search = search;
    }

    if (selectedClient !== "all") {
      params.clientId = selectedClient;
    }

    // Note: vesselId and bookingDate might not be officially supported by the backend
    // Commenting out for now, but keeping the logic for future backend support
    // if (selectedVessel !== "all") {
    //   params.vesselId = selectedVessel;
    // }

    if (selectedDate) {
      const date = dayjs(selectedDate);

      params.startDate = date.clone().startOf("day").toISOString();
      params.endDate = date.clone().endOf("day").toISOString();
    } else {
      params.startDate = undefined;
      params.endDate = undefined;
    }

    return params;
  }, [page, search, selectedClient, selectedDate]);

  const { data, isLoading } = useGetBookings({
    page,
    pageSize: 10,
    sortByCreatedAt: SortOrder.DESC,
    ...filterParams,
  });

  // Client-side filtering for vessel and date (since backend doesn't support these filters)
  const filteredBookings = useMemo(() => {
    if (!data?.items) return [];

    return data.items.filter((booking) => {
      // Filter by vessel (check service tickets for vessel assignment)
      const { filters } = useInventoryControlStore.getState();
      const { selectedVessel, selectedDate } = filters;

      if (selectedVessel !== "all") {
        const hasMatchingVessel = booking.serviceTicket?.some(
          (ticket) =>
            ticket.vessel?.id === selectedVessel ||
            ticket.vessel?.name === selectedVessel
        );
        if (!hasMatchingVessel) return false;
      }

      // Filter by date
      if (selectedDate) {
        const bookingDate =
          booking.bookingDate || booking.serviceTicket?.[0]?.serviceDatetime;
        if (bookingDate) {
          const bookingDateStr = new Date(bookingDate)
            .toISOString()
            .split("T")[0];
          if (bookingDateStr !== selectedDate) return false;
        }
      }

      return true;
    });
  }, [data?.items]);

  // Gather all inventories from selected service tickets
  const selectedInventories = useMemo(() => {
    if (!filteredBookings || selectedServiceTickets.length === 0) return [];

    const allInventories: TInventory[] = [];
    filteredBookings.forEach((booking) => {
      booking.serviceTicket
        .filter((ticket) => selectedServiceTickets.includes(ticket.id))
        .forEach((ticket) => {
          if (ticket.inventories) {
            allInventories.push(...ticket.inventories);
          }
        });
    });

    return allInventories;
  }, [filteredBookings, selectedServiceTickets]);

  // Pass selected inventories to parent
  useEffect(() => {
    onSelectedInventories(selectedInventories);
  }, [selectedInventories, onSelectedInventories]);

  const toggleServiceTicketSelection = (serviceTicketId: string) => {
    setSelectedServiceTickets((prev) =>
      prev.includes(serviceTicketId)
        ? prev.filter((id) => id !== serviceTicketId)
        : [...prev, serviceTicketId]
    );
  };

  const handleBookingServiceTicketsToggle = (booking: TBooking) => {
    const bookingServiceTicketIds = booking.serviceTicket.map(
      (ticket) => ticket.id
    );
    const allSelected = bookingServiceTicketIds.every((id) =>
      selectedServiceTickets.includes(id)
    );

    if (allSelected) {
      // Deselect all service tickets from this booking
      setSelectedServiceTickets((prev) =>
        prev.filter((id) => !bookingServiceTicketIds.includes(id))
      );
    } else {
      // Select all service tickets from this booking
      setSelectedServiceTickets((prev) => [
        ...new Set([...prev, ...bookingServiceTicketIds]),
      ]);
    }
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg border shadow-sm p-6">
        <div className="flex justify-center items-center h-64">
          <Spin size="large" />
        </div>
      </div>
    );
  }

  if (!filteredBookings?.length) {
    return (
      <div className="bg-white rounded-lg border shadow-sm p-6">
        <Empty description="No bookings found for the selected date" />
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border shadow-sm">
      <div className="p-4 border-b bg-gray-50">
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-600">
            {selectedServiceTickets.length > 0
              ? `${selectedServiceTickets.length} service tickets selected`
              : "Select service tickets to control inventory"}
          </span>
          <span className="text-sm text-gray-500">
            {selectedInventories.length} total inventory items selected
          </span>
        </div>
      </div>

      <div className="divide-y divide-gray-200">
        {filteredBookings.map((booking) => {
          const hasSelectedTickets = booking.serviceTicket.some((ticket) =>
            selectedServiceTickets.includes(ticket.id)
          );
          const allTicketsSelected = booking.serviceTicket.every((ticket) =>
            selectedServiceTickets.includes(ticket.id)
          );

          return (
            <div
              key={booking.id}
              className={`p-4 ${
                hasSelectedTickets ? "bg-blue-50" : "hover:bg-gray-50"
              } transition-colors`}
            >
              <div className="flex items-start space-x-4">
                <div onClick={(e) => e.stopPropagation()}>
                  <Checkbox
                    checked={allTicketsSelected}
                    indeterminate={hasSelectedTickets && !allTicketsSelected}
                    onChange={() => handleBookingServiceTicketsToggle(booking)}
                  />
                </div>

                <div className="flex-1 min-w-0">
                  {/* Booking Header */}
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <h3 className="text-sm font-medium text-gray-900">
                        {booking.referenceNumber ||
                          `Booking #${booking.id.slice(-6)}`}
                      </h3>
                      <p className="text-xs text-gray-500">
                        Client:{" "}
                        {booking.client?.companyName ||
                          `${booking.client?.firstName} ${booking.client?.lastName}` ||
                          "Unknown"}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-gray-500">
                        {new Date(
                          booking.bookingDate || booking.createdAt
                        ).toLocaleDateString()}
                      </p>
                    </div>
                  </div>

                  {/* Service Tickets */}
                  <div className="space-y-2">
                    {booking.serviceTicket.map((ticket) => (
                      <div
                        key={ticket.id}
                        className={`p-3 rounded border ${
                          selectedServiceTickets.includes(ticket.id)
                            ? "border-blue-300 bg-blue-50"
                            : "border-gray-200 bg-gray-50"
                        } cursor-pointer`}
                        onClick={() => toggleServiceTicketSelection(ticket.id)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <div onClick={(e) => e.stopPropagation()}>
                              <Checkbox
                                checked={selectedServiceTickets.includes(
                                  ticket.id
                                )}
                                onChange={() =>
                                  toggleServiceTicketSelection(ticket.id)
                                }
                              />
                            </div>

                            <div className="flex items-center space-x-2">
                              <FileSignature
                                className="text-blue-600"
                                size={16}
                              />
                              <span className="text-sm font-medium">
                                {capitalize(ticket.serviceType)}
                              </span>
                            </div>
                          </div>

                          <div className="flex items-center space-x-4 text-xs text-gray-500">
                            {ticket.vessel && (
                              <div className="flex items-center space-x-1">
                                <Truck size={12} />
                                <span>
                                  {ticket.vessel.name || ticket.vessel.id}
                                </span>
                              </div>
                            )}
                            <div className="flex items-center space-x-1">
                              <Clock size={12} />
                              <span>
                                {ticket.serviceDatetime
                                  ? new Date(
                                      ticket.serviceDatetime
                                    ).toLocaleDateString()
                                  : "No date set"}
                              </span>
                            </div>
                            <span>{ticket.inventories?.length || 0} items</span>
                          </div>
                        </div>

                        {/* Staff */}
                        {ticket.staffs && ticket.staffs.length > 0 && (
                          <div className="mt-2 flex items-center space-x-1 text-xs text-gray-500">
                            <User size={12} />
                            <span>
                              Staff:{" "}
                              {ticket.staffs
                                .map(
                                  (staff) =>
                                    `${staff.user?.firstName || ""} ${
                                      staff.user?.lastName || ""
                                    }`.trim() || staff.id
                                )
                                .join(", ")}
                            </span>
                          </div>
                        )}

                        {/* Inventory Preview */}
                        {ticket.inventories &&
                          ticket.inventories.length > 0 && (
                            <div className="mt-2 text-xs text-gray-600">
                              <div className="flex flex-wrap gap-1">
                                {ticket.inventories.slice(0, 3).map((item) => (
                                  <span
                                    key={item.id}
                                    className="inline-block px-2 py-1 bg-white rounded border"
                                  >
                                    {item.title ||
                                      item.inventoryRef ||
                                      "Untitled"}
                                  </span>
                                ))}
                                {ticket.inventories.length > 3 && (
                                  <span className="inline-block px-2 py-1 bg-gray-200 rounded text-gray-600">
                                    +{ticket.inventories.length - 3} more
                                  </span>
                                )}
                              </div>
                            </div>
                          )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Pagination */}
      <div className="p-4 border-t">
        <Pagination
          current={page}
          pageSize={10}
          total={data?.meta.totalItems || 0}
          onChange={setPage}
          showSizeChanger={false}
          showQuickJumper
          showTotal={(total, range) =>
            `${range[0]}-${range[1]} of ${total} bookings`
          }
        />
      </div>
    </div>
  );
};
